import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { check, nullable, number, object, pipe } from "valibot";
import {
  dataName,
  decimalValue,
  errorTranslation,
  label,
  optionalTextValue,
  rememberInput,
  required,
} from "../../utils/validation/base";
import { isNil } from "lodash-es";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { NumberInput } from "../../components/inputs/NumberInput";
import { MAX_CHARACTERS_SM, MAX_NUMBER_XL, MAX_NUMBER_XL_NEGATIVE, NUMBER_CONDITION_KEYS } from "../../constants";
import { defaultNumberFilter } from "../../pages/customer/form/filter";
import FormatNumber from "../../components/formatting/FormatNumber";
import { Toggle } from "../../components/toggle/Toggle";
import { InputPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { createMemo } from "solid-js";
import GlobalErrorInlineMessage from "../GlobalErrorInlineMessage";
import { keyFromSelector } from "i18next";

const Schema = pipe(
  object({
    label_text: label(),
    text_placeholder: optionalTextValue(MAX_CHARACTERS_SM),
    text_default_value: nullable(decimalValue(MAX_NUMBER_XL_NEGATIVE, MAX_NUMBER_XL)),
    numeric_min: nullable(decimalValue(MAX_NUMBER_XL_NEGATIVE, MAX_NUMBER_XL)),
    numeric_max: nullable(decimalValue(MAX_NUMBER_XL_NEGATIVE, MAX_NUMBER_XL)),
    data_name: dataName(),
    required: required(),
    remember_input: rememberInput(),
  }),
  check(
    ({ numeric_min, numeric_max }) => {
      const max = !isNil(numeric_max) ? Math.min(numeric_max, MAX_NUMBER_XL) : Infinity;
      const min = !isNil(numeric_min) ? Math.max(numeric_min, MAX_NUMBER_XL_NEGATIVE) : -Infinity;
      return max >= min;
    },
    errorTranslation(keyFromSelector(($) => $.VALIDATION_NUMERIC_MAX_GREATER_EQUALS_MIN)),
  ),
  check(
    ({ numeric_min, numeric_max, text_default_value }) =>
      isNil(text_default_value)
        ? true
        : text_default_value >= (numeric_min ?? -Infinity) && text_default_value <= (numeric_max ?? Infinity),
    errorTranslation(keyFromSelector(($) => $.VALIDATION_NUMBER_DEFAULT_OUTSIDE_MIN_MAX)),
  ),
);
const ValueSchema = number();

export type NumberFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const NumberField: Field<NumberFieldProperties, DefaultDtoType, ValueType> = {
  type: "number",
  category: "number",
  widget: "com.moreapps:number:1",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    text_placeholder: undefined,
    text_default_value: null,
    numeric_min: null,
    numeric_max: null,
    remember_input: false,
    required: false,
  }),
  ruleBehaviour: {
    conditions: { keys: NUMBER_CONDITION_KEYS, variant: "text" },
    actions: {
      variant: "text",
      setValue: {
        fieldTypes: ["calculation", "lookup", "number", "price", "radio", "rating", "search", "slider", "smiley"],
        variableTypes: ["number"],
      },
    },
  },
  filter: {
    type: "number",
    definition: defaultNumberFilter,
  },
  stringValue: (value) => value.toString(),
  renderValue: (value) => <FormatNumber value={value} />,
  icon: "listNumber",
  titleKey: keyFromSelector(($) => $.NUMBER_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.NUMBER_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    text_placeholder: keyFromSelector(($) => $.FIELD_PROPERTY_PLACEHOLDER),
    text_default_value: keyFromSelector(($) => $.FIELD_PROPERTY_DEFAULT_VALUE),
    numeric_min: keyFromSelector(($) => $.FIELD_PROPERTY_NUMERIC_MIN),
    numeric_max: keyFromSelector(($) => $.FIELD_PROPERTY_NUMERIC_MAX),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    remember_input: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_INPUT),
  },
  PreviewContent: (props) => (
    <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
      <InputPreview
        value={props.properties.text_default_value?.toString()}
        placeholder={props.properties.text_placeholder}
      />
    </PreviewContentLabel>
  ),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();
    const globalErrors = createMemo(() => props.getGlobalErrors());

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
          <TextInput
            label={t(props.propertyKeys.text_placeholder)}
            value={props.data.properties.text_placeholder!}
            onBlur={(e) => props.setValue("text_placeholder", e.target.value)}
            errorMessage={props.getError("text_placeholder")}
          />
          <NumberInput
            label={t(props.propertyKeys.text_default_value)}
            value={props.data.properties.text_default_value}
            minValue={MAX_NUMBER_XL_NEGATIVE}
            maxValue={MAX_NUMBER_XL}
            allowDecimal
            onBlur={(value) => props.setValue("text_default_value", value)}
            errorMessage={props.getError("text_default_value")}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
          <NumberInput
            label={t(props.propertyKeys.numeric_min)}
            value={props.data.properties.numeric_min}
            minValue={MAX_NUMBER_XL_NEGATIVE}
            maxValue={MAX_NUMBER_XL}
            allowDecimal
            onBlur={(value) => props.setValue("numeric_min", value)}
            errorMessage={props.getError("numeric_min")}
          />
          <NumberInput
            label={t(props.propertyKeys.numeric_max)}
            value={props.data.properties.numeric_max}
            minValue={MAX_NUMBER_XL_NEGATIVE}
            maxValue={MAX_NUMBER_XL}
            allowDecimal
            onBlur={(value) => props.setValue("numeric_max", value)}
            errorMessage={props.getError("numeric_max")}
          />
          <GlobalErrorInlineMessage errors={globalErrors()} />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(($) => $.REMEMBER_INPUT_SWITCH)}
            checked={props.data.properties.remember_input}
            setChecked={(checked) => props.setValue("remember_input", checked)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_INPUT_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(NumberField);
